import React, { FormEvent, useState } from 'react';
import { useMutation } from 'react-query';
import {
  Box,
  Heading,
  Text,
  Card,
  CardBody,
  Button,
  Link,
  Alert,
  ArrowRightIcon,
  ChevronLeftIcon,
  BillIcon,
  PayrollAddonsIcon,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TextInput,
  Dropdown,
  DropdownOverlay,
  SelectInput,
  ActionList,
  ActionListItem,
  Spinner,
} from '@razorpay/blade/components';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { concatenateAllowances } from './utils';
import Allowance, { amountTypes, taxableTypes } from 'utils/Allowance';
import api from 'api';
import { SalaryStructureRequest } from 'api/companyOnboarding';
import Errors from 'components/ui/Form/Errors';
import { AppError } from 'utils/AppError';
import { trackSegment } from 'utils/segment';
import { trackGtag, trackRazorpayAnalytics } from 'utils/razorpayAnalytics';
import {
  handleBackToSetupGuide,
  getDefaultSalaryStructureTableNodes,
  getCustomSalaryStructureTableNodes,
} from '../utils';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import IsFeatureDisabledForRearch from 'access/IsFeatureDisabledForRearch';
import { routePaths } from 'components/Routes/data';
import { useForm } from 'forms/useForm';
import { ValidationSchema } from 'forms/types';
import { ResetLocalOverridingStyles } from 'components/RunPayroll/ComplianceActions/styles';
import { StyledForm } from './styles';

type FixedFields = {
  customBasic: string;
  customDa: string;
  customHra: string;
  customSpecialAllowance: string;
  customLta: string;
  customNpsLimit: string;
};

const PercentageOptions = [
  { value: String(amountTypes.ALLOWANCE_AMOUNT_TYPE_PERCENTAGE), title: 'Percentage' },
  { value: String(amountTypes.ALLOWANCE_AMOUNT_TYPE_FIXED), title: 'Fixed' },
];

const TaxableOptions = [
  { value: String(taxableTypes.TYPE_YES), title: 'Yes' },
  { value: String(taxableTypes.TYPE_NO), title: 'No' },
  { value: String(taxableTypes.TYPE_FLEXI), title: 'Flexi' },
];

// Each validator first checks the field isn't blank — `Number("")` is 0,
// which would silently let an empty input pass and submit zero. The user
// must explicitly type "0" if they want to remove a component.
const fixedFieldValidation: ValidationSchema<FixedFields> = {
  customBasic: (data) => {
    const raw = data.customBasic ?? '';
    if (!raw.trim()) return 'This field is required';
    const val = Number(raw);
    if (isNaN(val) || val < 0) return 'Enter a valid percentage';
    return null;
  },
  customDa: (data) => {
    const raw = data.customDa ?? '';
    if (!raw.trim()) return 'This field is required';
    const val = Number(raw);
    if (isNaN(val) || val < 0) return 'Enter a valid percentage';
    return null;
  },
  customHra: (data) => {
    const raw = data.customHra ?? '';
    if (!raw.trim()) return 'This field is required';
    const val = Number(raw);
    if (isNaN(val) || val < 0) return 'Enter a valid percentage';
    return null;
  },
  customSpecialAllowance: (data) => {
    const raw = data.customSpecialAllowance ?? '';
    if (!raw.trim()) return 'This field is required';
    const val = Number(raw);
    if (isNaN(val) || val < 0) return 'Enter a valid percentage';
    return null;
  },
  customLta: (data) => {
    const raw = data.customLta ?? '';
    if (!raw.trim()) return 'This field is required';
    const val = Number(raw);
    if (isNaN(val) || val < 0) return 'Enter a valid percentage';
    return null;
  },
  customNpsLimit: (data) => {
    const raw = data.customNpsLimit ?? '';
    if (!raw.trim()) return 'This field is required';
    const val = Number(raw);
    if (isNaN(val) || val < 0) return 'Enter a valid percentage';
    return null;
  },
};

const SalaryStructure = () => {
  const currentOrganization = useSelector(loggedInUserSelectors.currentOrganization);
  const { isCustomSalaryStructureDisabled, isEmployerNPSDisabled } =
    useIsFeatureDisabledForRearch();
  const salaryStructureConfig = currentOrganization?.salaryStructureConfig;
  const defaultSalaryCustomAllowancesAllowed =
    useSelector(loggedInUserSelectors.appConfig).defaultSalaryCustomAllowancesAllowed || 0;
  const organizationCustomAllowances = useSelector(
    loggedInUserSelectors.organizationCustomAllowances,
  );
  const organizationDefaultSalaryStructure = useSelector(
    loggedInUserSelectors.organizationDefaultSalaryStructure,
  );
  const links = useSelector(loggedInUserSelectors.referenceLinks);
  const [shouldUseDefaultSalaryStructure, setShouldUseDefaultSalaryStructure] = useState(
    isCustomSalaryStructureDisabled || !!salaryStructureConfig?.useDefault,
  );
  const [shouldFlexibleBenefitsBeTaxableByDefault, setShouldFlexibleBenefitsBeTaxableByDefault] =
    useState(!!salaryStructureConfig?.fbpDefaultTaxable);
  const [customAllowances, setCustomAllowances] = useState(() =>
    concatenateAllowances(organizationCustomAllowances, defaultSalaryCustomAllowancesAllowed),
  );
  const [isRedirectingHome, setIsRedirectingHome] = useState(false);

  const { formData, register, validate, setIsSubmitting } = useForm<FixedFields>({
    customBasic: String(salaryStructureConfig?.percBasic || 0),
    customDa: String(salaryStructureConfig?.percDa || 0),
    customHra: String(salaryStructureConfig?.percHra || 0),
    customSpecialAllowance: String(salaryStructureConfig?.percSpecialAllowance || 0),
    customLta: String(salaryStructureConfig?.percLta || 0),
    customNpsLimit: String(salaryStructureConfig?.percNpsLimit || 0),
  });

  const updateSalaryStructureMutation = useMutation(
    (req: SalaryStructureRequest) => api.companyOnboarding.updateSalaryStructure(req),
    {
      onError: (_error: typeof AppError) => { },
      onSuccess: () => {
        trackSegment(
          'onboarding.company_details.set_employee_salary_structure.continue_click',
          { queue: true },
        );
        triggerOnboardingPixels();
        setIsRedirectingHome(true);
        window.location.href = routePaths.phpRoutes.dashboard;
      },
    },
  );

  const triggerOnboardingPixels = () => {
    trackRazorpayAnalytics('fb', 'Emp_Salary_Structure-Payroll');
    trackRazorpayAnalytics('linkedin_onboarding_salary_structure', { conversion_id: 7377308 });
    trackRazorpayAnalytics('twitter_onboarding_salary_structure', {
      tw_sale_amount: 0,
      tw_order_quantity: 0,
    });
    trackGtag('event', 'conversion', { send_to: 'AW-928471290/zzc7CLSC_rIDEPqx3boD' });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    let customBasic = 0;
    let customHra = 0;
    let customSpecialAllowance = 0;
    let customLta = 0;
    let customDa = 0;
    let customNpsLimit = 0;
    const allowances: Allowance[] = [];

    if (!shouldUseDefaultSalaryStructure) {
      if (!validate(fixedFieldValidation)) return;

      customBasic = parseInt(formData.customBasic || '0');
      customHra = parseInt(formData.customHra || '0');
      customSpecialAllowance = parseInt(formData.customSpecialAllowance || '0');
      customLta = parseInt(formData.customLta || '0');
      customDa = parseInt(formData.customDa || '0');
      customNpsLimit = parseInt(formData.customNpsLimit || '0');

      customAllowances.forEach((allowance) => {
        // Skip rows the user hasn't really filled in: missing name OR a
        // zero/negative value. The server requires `value >= 1`, so a row
        // with value 0 is treated the same as an unfilled row and dropped.
        if (allowance.name && (allowance.value ?? 0) > 0) {
          allowances.push(
            new Allowance({
              name: allowance.name,
              value: allowance.value ?? 0,
              amountType: allowance.amountType,
              taxableType: allowance.taxableType,
            }),
          );
        }
      });
    }

    updateSalaryStructureMutation.mutate({
      customBasic,
      customHra,
      customSpecialAllowance,
      customLta,
      customDa,
      customNpsLimit,
      customAllowances: allowances,
      shouldFlexibleBenefitsBeTaxableByDefault,
      shouldUseDefaultSalaryStructure,
    });
  };

  const updateCustomAllowance = (idx: number, field: keyof Allowance, value: string | number) => {
    setCustomAllowances((prev) =>
      prev.map((a, i) => {
        if (i !== idx) return a;
        return new Allowance({
          name: field === 'name' ? String(value) : a.name,
          // Clamp value to a non-negative integer at the point of state-set:
          // empty / NaN / negative all become 0, decimals are floored.
          // Guarantees state never holds an invalid amount.
          value:
            field === 'value'
              ? Math.max(0, parseInt(String(value), 10) || 0)
              : a.value,
          amountType: field === 'amountType' ? Number(value) : a.amountType,
          taxableType: field === 'taxableType' ? Number(value) : a.taxableType,
        });
      }),
    );
  };

  const isFormBeingSubmitted = updateSalaryStructureMutation.status === 'loading';
  const isEmployerNPSEnabled = !isEmployerNPSDisabled && !!currentOrganization?.npsConfig?.isNpsEnabled;

  if (isRedirectingHome) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Spinner accessibilityLabel="Redirecting to dashboard" />
      </Box>
    );
  }

  return (
    <ResetLocalOverridingStyles>
      <Box maxWidth="640px" marginX="auto">
        <StyledForm onSubmit={handleSubmit}>
          {updateSalaryStructureMutation.status === 'error' && (
            <Box marginBottom="spacing.5">
              <Alert
                color="negative"
                emphasis="subtle"
                isDismissible={false}
                description={<Errors errorResponse={updateSalaryStructureMutation.error} />}
              />
            </Box>
          )}

          <Box marginBottom="spacing.5">
            <Card padding="spacing.7" elevation="none">
              <CardBody>
                <Heading size="small" weight="semibold" color="surface.text.gray.normal">
                  How will salaries be structured?
                </Heading>
                <Box
                  display="flex"
                  gap="spacing.7"
                  marginTop="spacing.5"
                  marginBottom="spacing.6"
                  alignItems="stretch">
                  <Box flex={1}>
                    <Card
                      width="100%"
                      height="100%"
                      isSelected={shouldUseDefaultSalaryStructure}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isFormBeingSubmitted) setShouldUseDefaultSalaryStructure(true);
                      }}
                      elevation="none"
                      padding="spacing.7"
                      borderRadius="large"
                      backgroundColor="surface.background.gray.intense"
                    >
                      <CardBody>
                        <Box display="flex" flexDirection="column" gap="spacing.5">
                          <BillIcon color="surface.icon.gray.normal" size="medium" />
                          <Box display="flex" flexDirection="column" gap="spacing.1">
                            <Text weight="semibold" size="medium" color="surface.text.gray.normal" textAlign="left">
                              Use default
                            </Text>
                            <Text size="small" color="surface.text.gray.muted" textAlign="left">
                              Recommended for most companies
                            </Text>
                          </Box>
                        </Box>
                      </CardBody>
                    </Card>
                  </Box>

                  {!isCustomSalaryStructureDisabled && (
                    <Box flex={1}>
                      <Card
                        width="100%"
                        height="100%"
                        isSelected={!shouldUseDefaultSalaryStructure}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!isFormBeingSubmitted) setShouldUseDefaultSalaryStructure(false);
                        }}
                        elevation="none"
                        padding="spacing.7"
                        borderRadius="large"
                        backgroundColor="surface.background.gray.intense"
                      >
                        <CardBody>
                          <Box display="flex" flexDirection="column" gap="spacing.5">
                            <PayrollAddonsIcon color="surface.icon.gray.normal" size="medium" />
                            <Box display="flex" flexDirection="column" gap="spacing.1">
                              <Text weight="semibold" size="medium" color="surface.text.gray.normal" textAlign="left">
                                Customise
                              </Text>
                              <Text size="small" color="surface.text.gray.muted" textAlign="left">
                                Set your own salary components
                              </Text>
                            </Box>
                          </Box>
                        </CardBody>
                      </Card>
                    </Box>
                  )}
                </Box>

                <Text
                  size="small"
                  color="surface.text.gray.muted"
                  marginBottom="spacing.4"
                  textAlign="left"
                >
                  How each employee's CTC is split. PF and ESI contributions are added on top —
                  final ratios may differ slightly.
                </Text>

                {shouldUseDefaultSalaryStructure ? (
                  <Box marginBottom="spacing.8">
                    <Table
                      data={{
                        nodes: getDefaultSalaryStructureTableNodes({ organizationDefaultSalaryStructure }),
                      }}
                      gridTemplateColumns="2fr 1fr 1fr 1fr"
                      showBorderedCells={false}>
                      {(tableData) => (
                        <>
                          <TableHeader>
                            <TableHeaderRow>
                              <TableHeaderCell>Salary Component</TableHeaderCell>
                              <TableHeaderCell>%age of CTC</TableHeaderCell>
                              <TableHeaderCell>Percentage or Fixed?</TableHeaderCell>
                              <TableHeaderCell>Taxable?</TableHeaderCell>
                            </TableHeaderRow>
                          </TableHeader>
                          <TableBody>
                            {tableData.map((row) => (
                              <TableRow key={row.id} item={row}>
                                <TableCell>{row.component}</TableCell>
                                <TableCell>{row.percentage}</TableCell>
                                <TableCell>Percentage</TableCell>
                                <TableCell>Yes</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </>
                      )}
                    </Table>
                  </Box>
                ) : (
                  <>
                    <Alert
                      color="information"
                      emphasis="subtle"
                      isDismissible={false}
                      description={
                        <Box display="flex" flexDirection="column" gap="spacing.2">
                          <Text size="small" color="surface.text.gray.subtle" textAlign="left">
                            1. To remove any predefined component, enter "0" as the
                            percentage/amount.
                          </Text>
                          <Text size="small" color="surface.text.gray.subtle" textAlign="left">
                            2. Fixed amounts should be entered as per monthly salary.
                          </Text>
                          <Text size="small" color="surface.text.gray.subtle" textAlign="left">
                            3. If the sum of components doesn't match with monthly pay, the
                            difference amount will be adjusted from these components - LTA, HRA,
                            Special Allowances, Basic (Where LTA has the highest priority and Basic
                            has the lowest).
                          </Text>
                        </Box>
                      }
                    />
                    <Box marginTop="spacing.5" marginBottom="spacing.8">
                      <Table
                        gridTemplateColumns="2fr 1fr 1fr 1fr"
                        data={{
                          nodes: getCustomSalaryStructureTableNodes({
                            salaryStructureConfig,
                            isEmployerNPSEnabled,
                            customAllowances,
                          }),
                        }}
                        showBorderedCells={false}>
                        {(tableData) => (
                          <>
                            <TableHeader>
                              <TableHeaderRow>
                                <TableHeaderCell>Salary Component</TableHeaderCell>
                                <TableHeaderCell>%age of CTC</TableHeaderCell>
                                <TableHeaderCell>Percentage or Fixed?</TableHeaderCell>
                                <TableHeaderCell>Taxable?</TableHeaderCell>
                              </TableHeaderRow>
                            </TableHeader>
                            <TableBody>
                              {tableData.map((row) =>
                                row.kind === 'fixed' ? (
                                  <TableRow key={row.id} item={row}>
                                    <TableCell>{row.label}</TableCell>
                                    <TableCell>
                                      <TextInput
                                        accessibilityLabel={row.label}
                                        type="number"
                                        size="medium"
                                        {...register(row.stateKey as keyof FixedFields)}
                                        isDisabled={isFormBeingSubmitted}
                                        isRequired
                                      />
                                    </TableCell>
                                    <TableCell>Percentage</TableCell>
                                    <TableCell>
                                      {row.taxable === 'PartiallyHRA' ? (
                                        <Link href={links.linkHra} target="_blank" rel="noreferrer">
                                          Partially
                                        </Link>
                                      ) : (
                                        row.taxable
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  <TableRow key={row.id} item={row}>
                                    <TableCell>
                                      <TextInput
                                        accessibilityLabel={`Custom allowance ${row.idx + 1} name`}
                                        placeholder="Custom allowance"
                                        size="medium"
                                        value={row.allowance.name}
                                        onChange={({ value }) => updateCustomAllowance(row.idx, 'name', value ?? '')}
                                        isDisabled={isFormBeingSubmitted}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextInput
                                        accessibilityLabel={`Custom allowance ${row.idx + 1} value`}
                                        type="number"
                                        size="medium"
                                        value={String(row.allowance.value ?? '')}
                                        onChange={({ value }) => updateCustomAllowance(row.idx, 'value', value ?? '0')}
                                        isDisabled={isFormBeingSubmitted}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Box minWidth="120px">
                                        <Dropdown selectionType="single">
                                          <SelectInput
                                            accessibilityLabel={`Custom allowance ${row.idx + 1} type`}
                                            size="medium"
                                            value={String(row.allowance.amountType)}
                                            onChange={({ values }) => {
                                              if (values[0]) updateCustomAllowance(row.idx, 'amountType', values[0]);
                                            }}
                                            isDisabled={isFormBeingSubmitted}
                                          />
                                          <DropdownOverlay>
                                            <ActionList>
                                              {PercentageOptions.map((opt) => (
                                                <ActionListItem key={opt.value} title={opt.title} value={opt.value} />
                                              ))}
                                            </ActionList>
                                          </DropdownOverlay>
                                        </Dropdown>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Box minWidth="120px">
                                        <Dropdown selectionType="single">
                                          <SelectInput
                                            accessibilityLabel={`Custom allowance ${row.idx + 1} taxable`}
                                            size="medium"
                                            value={String(row.allowance.taxableType)}
                                            onChange={({ values }) => {
                                              if (values[0]) updateCustomAllowance(row.idx, 'taxableType', values[0]);
                                            }}
                                            isDisabled={isFormBeingSubmitted}
                                          />
                                          <DropdownOverlay>
                                            <ActionList>
                                              {TaxableOptions.map((opt) => (
                                                <ActionListItem key={opt.value} title={opt.title} value={opt.value} />
                                              ))}
                                            </ActionList>
                                          </DropdownOverlay>
                                        </Dropdown>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ),
                              )}
                            </TableBody>
                          </>
                        )}
                      </Table>
                    </Box>
                  </>
                )}
              </CardBody>
            </Card>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" marginTop="spacing.6">
            <Link icon={ChevronLeftIcon} iconPosition="left" onClick={handleBackToSetupGuide}>
              Back to setup guide
            </Link>
            <Button
              type="submit"
              variant="primary"
              icon={ArrowRightIcon}
              iconPosition="right"
              isLoading={isFormBeingSubmitted}
            >
              Save &amp; Next
            </Button>
          </Box>
        </StyledForm>
      </Box>
    </ResetLocalOverridingStyles>
  );
};

export default SalaryStructure;
